import Dexie, { type EntityTable, type Table, type Transaction } from 'dexie';
import type { Action, RemoteEvent, Tournament } from '@airmail/engine';

/**
 * Persistenz (docs/08). Bis Version 3 schrieb jede Aktion den kompletten
 * Snapshot samt Aktionsprotokoll (`t.log`) — bei 64 Teams rund 300 KB je
 * Tipp, dazu das Sync-Profil mit seiner Kopie des Server-Protokolls. Seit
 * Version 4 liegen die wachsenden Listen in eigenen Tabellen und werden nur
 * noch **angehängt**:
 *
 * - `tournaments`: Stand **ohne** Protokoll im Feld `snapshot` (dessen `log`
 *   ist leer) plus `logLength` und `rev` (Schreibmarke, wechselt mit jedem
 *   Speichern).
 * - `logs`: das Protokoll, je Eintrag eine Zeile `[tournamentId+seq]`.
 * - `summaries`: was die Turnierliste zeigt — die Liste liest keine Stände mehr.
 * - `sync`: Sync-Profil ohne das Feld `remote`, dazu `remoteLength`/`remoteRev`.
 * - `remote`: die Server-Ereignisse des Profils, je Ereignis eine Zeile.
 *
 * Stand, Protokoll und Zusammenfassung werden immer in **einer** Transaktion
 * geschrieben. Geladen wird wieder ein vollständiges `Tournament` mit `log` —
 * der Rest der App merkt vom Umbau nichts.
 *
 * Warum `snapshot` statt `state` und das Profil ganz ohne `remote`: Ein noch
 * offenes Fenster mit dem **alten** Stand der App (Service Worker, Update erst
 * nach Bestätigung) liest dieselbe Datenbank. Fände es dort `state` mit leerem
 * Protokoll oder `remote: []`, hielte es beides für echt — und schriebe beim
 * nächsten Speichern ein Turnier ohne Protokoll zurück bzw. baute den Stand
 * aus einem leeren Server-Protokoll neu. So findet es nichts Brauchbares und
 * bleibt bei dem, was es im Speicher hat; was es selbst schreibt, ist das alte
 * Format mit vollem Protokoll, und das liest diese Version weiterhin.
 */
export interface TournamentRow {
  id: string;
  name: string;
  date?: string;
  status: Tournament['status'];
  updatedAt: number;
  /** Stand ohne Protokoll (`log` leer, das Protokoll steht in `logs`) — ab Version 4 */
  snapshot?: Tournament;
  /** Stand samt Protokoll — nur Zeilen im alten Format (bis Version 3) */
  state?: Tournament;
  /** Einträge in `logs` — fehlt bei einer Zeile im alten Format */
  logLength?: number;
  /** Schreibmarke — steht gleich in `summaries` */
  rev?: string;
}

export interface LogRow {
  tournamentId: string;
  seq: number;
  action: Action;
}

export interface RemoteRow {
  tournamentId: string;
  seq: number;
  event: RemoteEvent;
}

/** Eine Zeile der Turnierliste — ohne den ganzen Stand */
export interface TournamentSummary {
  id: string;
  name: string;
  date?: string;
  status: Tournament['status'];
  updatedAt: number;
  competitionName?: string;
  teams: number;
  courts: number;
  /** Schreibmarke und Protokoll-Länge wie in `tournaments` — für den Abgleich beim Anhängen */
  rev?: string;
  logLength?: number;
  /**
   * `aus`: **nicht** in der Cloud sichern. Gesetzt, wenn das Turnier aus der
   * Cloud gelöscht, die lokale Kopie aber behalten wurde — sonst lud die
   * automatische Sicherung (Turnierliste, Öffnen) es im nächsten Augenblick
   * wieder hoch. Fällt weg, sobald der Sync wieder eingeschaltet wird
   * (`enableSync`, store/sync.ts). Kein neues Schema: nur ein optionales Feld.
   */
  cloud?: 'aus';
}

/** Was hier vom Sync-Profil gebraucht wird — der Rest gehört `store/sync.ts` */
export interface SyncProfilBasis {
  tournamentId: string;
  remote: RemoteEvent[];
}
/** Gespeichertes Profil: im alten Format mit `remote`, im neuen mit `remoteLength`/`remoteRev` statt dessen */
type SyncZeile = Omit<SyncProfilBasis, 'remote'> & { remote?: RemoteEvent[]; remoteLength?: number; remoteRev?: string };

export const db = new Dexie('airmail') as Dexie & {
  tournaments: EntityTable<TournamentRow, 'id'>;
  logs: Table<LogRow, [string, number]>;
  summaries: EntityTable<TournamentSummary, 'id'>;
  remote: Table<RemoteRow, [string, number]>;
};

// Alle Schema-Versionen an einer Stelle — Tabellen anderer Module (Saison, Sync)
// werden dort per db.table(name) zur Laufzeit geholt, nie beim Modul-Laden.
db.version(1).stores({ tournaments: 'id, updatedAt, status' });
db.version(2).stores({ tournaments: 'id, updatedAt, status', seasons: 'id, updatedAt' });
db.version(3).stores({ tournaments: 'id, updatedAt, status', seasons: 'id, updatedAt', sync: 'tournamentId' });
db.version(4)
  .stores({
    tournaments: 'id, updatedAt, status',
    seasons: 'id, updatedAt',
    sync: 'tournamentId',
    logs: '[tournamentId+seq]',
    summaries: 'id, updatedAt',
    remote: '[tournamentId+seq]',
  })
  .upgrade((tx) => umbauV4(tx));

export function newId(): string {
  const bytes = new Uint8Array(6);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

// ---------------------------------------------------------------------------
// Umbau auf Version 4
// ---------------------------------------------------------------------------

/**
 * Bestehende Daten umbauen — läuft in der Upgrade-Transaktion, also ganz oder
 * gar nicht. Zeile für Zeile, damit nie alle Stände gleichzeitig im Speicher
 * liegen. Wiederholbar: Schon umgebaute Zeilen (`logLength`/`remoteLength`
 * gesetzt) bleiben, wie sie sind. Eine Zeile, die sich nicht umbauen lässt,
 * bleibt **unverändert** im alten Format liegen — `loadTournament` und
 * `loadSyncProfile` lesen beide Formate, und das nächste Speichern schreibt
 * sie im neuen. Verloren geht dabei nichts.
 */
export async function umbauV4(tx: Transaction): Promise<void> {
  const tournaments = tx.table('tournaments');
  const logs = tx.table('logs');
  const summaries = tx.table('summaries');
  for (const key of await tournaments.toCollection().primaryKeys()) {
    const id = String(key);
    let row: Partial<TournamentRow> | undefined;
    try {
      row = (await tournaments.get(key)) as Partial<TournamentRow> | undefined;
    } catch (e) {
      console.warn('Umbau: Turnier nicht lesbar, bleibt wie es war', id, e);
      continue;
    }
    if (!row || typeof row !== 'object') continue;
    let neu: Partial<TournamentRow> = row;
    const state = row.state as Partial<Tournament> | undefined;
    if (typeof row.logLength !== 'number' && state && typeof state === 'object' && Array.isArray(state.log)) {
      try {
        const log = state.log;
        const rev = newId();
        const { state: _alt, ...kopf } = row;
        neu = { ...kopf, snapshot: { ...state, log: [] } as Tournament, logLength: log.length, rev };
        await alleVon(logs, id).delete();
        if (log.length) await logs.bulkPut(log.map((action, seq) => ({ tournamentId: id, seq, action })));
        await tournaments.put(neu);
      } catch (e) {
        console.warn('Umbau: Turnier bleibt im alten Format', id, e);
        neu = row;
      }
    }
    try {
      await summaries.put(zusammenfassung(id, neu));
    } catch (e) {
      console.warn('Umbau: keine Zusammenfassung', id, e);
    }
  }

  const sync = tx.table('sync');
  const remote = tx.table('remote');
  for (const key of await sync.toCollection().primaryKeys()) {
    const id = String(key);
    try {
      const row = (await sync.get(key)) as SyncZeile | undefined;
      if (!row || typeof row !== 'object' || typeof row.remoteLength === 'number' || !Array.isArray(row.remote)) continue;
      const events = row.remote;
      await alleVon(remote, id).delete();
      if (events.length) await remote.bulkPut(events.map((event, seq) => ({ tournamentId: id, seq, event })));
      const { remote: _alt, ...rest } = row;
      await sync.put({ ...rest, remoteLength: events.length, remoteRev: newId() });
    } catch (e) {
      console.warn('Umbau: Sync-Profil bleibt im alten Format', id, e);
    }
  }
}

/**
 * Zusammenfassung aus einer Zeile — auch aus einer halb kaputten: Was fehlt,
 * wird geschätzt, damit das Turnier in der Liste steht und sich öffnen (oder
 * wenigstens löschen) lässt.
 */
function zusammenfassung(id: string, row: Partial<TournamentRow>): TournamentSummary {
  const roh = row.snapshot ?? row.state;
  const state = (roh && typeof roh === 'object' ? roh : {}) as Partial<Tournament>;
  const comp = Array.isArray(state.competitions) ? state.competitions[0] : undefined;
  const name = typeof row.name === 'string' ? row.name : typeof state.name === 'string' ? state.name : id;
  const s: TournamentSummary = {
    id,
    name,
    status: row.status ?? state.status ?? 'draft',
    updatedAt: typeof row.updatedAt === 'number' ? row.updatedAt : 0,
    teams: Array.isArray(comp?.teams) ? comp.teams.length : 0,
    courts: Array.isArray(state.courts) ? state.courts.length : 0,
  };
  const date = row.date ?? state.date;
  if (typeof date === 'string' && date) s.date = date;
  if (typeof comp?.name === 'string' && comp.name) s.competitionName = comp.name;
  if (typeof row.logLength === 'number' && row.rev) {
    s.logLength = row.logLength;
    s.rev = row.rev;
  }
  return s;
}

// ---------------------------------------------------------------------------
// Anhängen statt neu schreiben
// ---------------------------------------------------------------------------

/**
 * Was dieses Fenster zuletzt geschrieben oder gelesen hat — je Turnier bzw.
 * Sync-Profil die Schreibmarke und die Liste, die dazu in der Datenbank steht.
 * Steht beim Speichern noch dieselbe Marke in der Datenbank, hat niemand
 * dazwischen geschrieben, und es genügt, ab dem ersten Unterschied zu
 * schreiben. Sonst (anderes Fenster, erstes Speichern, Zeile im alten Format)
 * wird die Liste des Turniers vollständig neu geschrieben.
 */
const bekannt = new Map<string, { rev: string; liste: readonly unknown[]; updatedAt?: number }>();

/** Speichervorgänge je Schlüssel nacheinander — sonst überholte ein späterer den früheren beim Merken der Marke */
const ketten = new Map<string, Promise<unknown>>();
function nacheinander<T>(key: string, fn: () => Promise<T>): Promise<T> {
  const p = (ketten.get(key) ?? Promise.resolve()).then(fn, fn);
  ketten.set(key, p);
  const weg = () => {
    if (ketten.get(key) === p) ketten.delete(key);
  };
  p.then(weg, weg);
  return p;
}

/**
 * Wie viele Einträge am Anfang gleich sind. Meist sind es dieselben Objekte
 * (die Engine hängt nur an); nach einem Zusammenführen sind es Kopien — dann
 * entscheidet der Inhalt.
 */
export function gemeinsamerAnfang(neu: readonly unknown[], alt: readonly unknown[]): number {
  const n = Math.min(neu.length, alt.length);
  let i = 0;
  while (i < n && (neu[i] === alt[i] || JSON.stringify(neu[i]) === JSON.stringify(alt[i]))) i++;
  return i;
}

/**
 * Eine Liste (Protokoll oder Server-Ereignisse) in ihre Tabelle schreiben:
 * ab `ab` löschen und neu anlegen. Danach stehen für `id` genau die Zeilen
 * 0 … liste.length-1 in der Tabelle.
 */
async function listeSchreiben<T>(table: Table, id: string, liste: readonly T[], ab: number, bisher: number, zeile: (x: T, seq: number) => object): Promise<void> {
  if (ab < bisher) await table.where('[tournamentId+seq]').between([id, ab], [id, Dexie.maxKey], true, true).delete();
  if (liste.length > ab) await table.bulkPut(liste.slice(ab).map((x, i) => zeile(x, ab + i)));
}

/** Ab wo muss geschrieben werden? Gibt `bisher = Infinity` zurück, wenn alles neu muss */
function abWo(schluessel: string, rev: string | undefined, laenge: number | undefined, liste: readonly unknown[]): { ab: number; bisher: number } {
  const b = bekannt.get(schluessel);
  if (!b || !rev || rev !== b.rev || laenge !== b.liste.length) return { ab: 0, bisher: Infinity };
  return { ab: gemeinsamerAnfang(liste, b.liste), bisher: b.liste.length };
}

/** Alle Zeilen eines Turniers in `logs` bzw. `remote` */
const alleVon = (table: Table, id: string) => table.where('[tournamentId+seq]').between([id, Dexie.minKey], [id, Dexie.maxKey], true, true);

// ---------------------------------------------------------------------------
// Turniere
// ---------------------------------------------------------------------------

/**
 * Turnier speichern: Stand ohne Protokoll, die neuen Protokoll-Einträge und
 * die Zusammenfassung — in einer Transaktion. Wurde das Protokoll umgeschrieben
 * (Rückgängig, Zusammenführen, Ersetzen), fliegt ab der ersten Abweichung alles
 * raus und wird neu angelegt, ebenfalls in derselben Transaktion.
 */
export function saveTournament(t: Tournament): Promise<void> {
  return nacheinander(`t:${t.id}`, async () => {
    const log = t.log;
    const rev = newId();
    const row: TournamentRow = { id: t.id, name: t.name, status: t.status, updatedAt: Date.now(), snapshot: { ...t, log: [] }, logLength: log.length, rev };
    if (t.date) row.date = t.date;
    const summary = zusammenfassung(t.id, row);
    await db.transaction('rw', db.tournaments, db.logs, db.summaries, async () => {
      const vorher = await db.summaries.get(t.id);
      const { ab, bisher } = abWo(`t:${t.id}`, vorher?.rev, vorher?.logLength, log);
      await listeSchreiben(db.logs, t.id, log, ab, bisher, (action, seq) => ({ tournamentId: t.id, seq, action }));
      await db.tournaments.put(row);
      // „nicht in der Cloud" überlebt jedes Speichern — die Zusammenfassung wird sonst aus dem Stand neu gebaut
      if (vorher?.cloud) summary.cloud = vorher.cloud;
      await db.summaries.put(summary);
    });
    bekannt.set(`t:${t.id}`, { rev, liste: log, updatedAt: row.updatedAt });
  });
}

/**
 * Turnier laden — mit Protokoll, genau wie es gespeichert wurde. Fehlen
 * Protokoll-Einträge, gibt es einen Fehler statt eines stillschweigend
 * gekürzten Protokolls (die Oberfläche bietet „Neu laden" an).
 */
export async function loadTournament(id: string): Promise<Tournament | undefined> {
  const geladen = await db.transaction('r', db.tournaments, db.logs, async () => {
    const row = await db.tournaments.get(id);
    if (!row) return undefined;
    // Altes Format (Umbau ging für diese Zeile nicht, oder ein Fenster mit altem Stand hat geschrieben): Protokoll steht im Stand
    if (typeof row.logLength !== 'number' || !row.snapshot) return row.state ? { state: row.state } : undefined;
    const zeilen = await db.logs.where('[tournamentId+seq]').between([id, 0], [id, row.logLength], true, false).toArray();
    if (zeilen.length !== row.logLength || zeilen.some((z, i) => z.seq !== i)) throw new Error(`Aktionsprotokoll unvollständig (${zeilen.length} von ${row.logLength} Einträgen)`);
    const log = zeilen.map((z) => z.action);
    // An derselben Stelle einsetzen — die Reihenfolge der Felder bleibt wie gespeichert
    const state = row.snapshot;
    state.log = log;
    return { state, rev: row.rev, updatedAt: row.updatedAt };
  });
  if (!geladen) return undefined;
  if (geladen.rev) bekannt.set(`t:${id}`, { rev: geladen.rev, liste: geladen.state.log, updatedAt: geladen.updatedAt });
  else bekannt.delete(`t:${id}`);
  return geladen.state;
}

/**
 * Steht in der Datenbank noch der Stand, den dieses Fenster zuletzt geladen
 * oder geschrieben hat? Liest nur die Zusammenfassung und den Index über
 * `updatedAt` (den ändert auch ein Fenster mit altem Stand der App, das von
 * `summaries` nichts weiß) — ein anderes Fenster spart sich damit das
 * Nachladen, wenn die Meldung nichts Neues bringt.
 */
export async function istAktuell(id: string): Promise<boolean> {
  const b = bekannt.get(`t:${id}`);
  if (!b || b.updatedAt === undefined) return false;
  const [s, ids] = await Promise.all([db.summaries.get(id), db.tournaments.where('updatedAt').equals(b.updatedAt).primaryKeys()]);
  return !!s?.rev && s.rev === b.rev && ids.includes(id);
}

/** Gibt es das Turnier auf diesem Gerät? Liest nur den Schlüssel */
export async function turnierVorhanden(id: string): Promise<boolean> {
  return (await db.tournaments.where('id').equals(id).count()) > 0;
}

/**
 * Turnierliste aus der Tabelle `summaries` — ohne einen einzigen Stand zu lesen.
 * Fehlt einem Turnier die Zusammenfassung (sollte nicht vorkommen), wird sie
 * aus der Zeile nachgebaut und gespeichert.
 */
export async function listTournamentSummaries(): Promise<TournamentSummary[]> {
  const [liste, ids] = await Promise.all([db.summaries.orderBy('updatedAt').reverse().toArray(), db.tournaments.toCollection().primaryKeys()]);
  const da = new Set(liste.map((s) => s.id));
  const fehlend = (ids as string[]).filter((id) => !da.has(id));
  if (fehlend.length === 0) return liste;
  for (const id of fehlend) {
    const row = await db.tournaments.get(id);
    if (!row) continue;
    const s = zusammenfassung(id, row);
    await db.summaries.put(s).catch(() => undefined);
    liste.push(s);
  }
  return liste.sort((a, b) => b.updatedAt - a.updatedAt);
}

/**
 * „Nicht in der Cloud sichern" setzen oder aufheben (siehe `TournamentSummary.cloud`).
 * Läuft in derselben Kette wie das Speichern, damit kein gleichzeitiges
 * `saveTournament` die Zusammenfassung dazwischen überschreibt.
 */
export function setzeOhneCloud(id: string, aus: boolean): Promise<void> {
  return nacheinander(`t:${id}`, async () => {
    const s = await db.summaries.get(id);
    if (!s || (s.cloud === 'aus') === aus) return;
    if (aus) s.cloud = 'aus';
    else delete s.cloud;
    await db.summaries.put(s);
  });
}

/** Steht das Turnier auf „nicht in der Cloud sichern"? */
export async function istOhneCloud(id: string): Promise<boolean> {
  return (await db.summaries.get(id))?.cloud === 'aus';
}

export async function deleteTournament(id: string): Promise<void> {
  await nacheinander(`t:${id}`, async () => {
    await db.transaction('rw', db.tournaments, db.logs, db.summaries, async () => {
      await db.tournaments.delete(id);
      await db.summaries.delete(id);
      await alleVon(db.logs, id).delete();
    });
    bekannt.delete(`t:${id}`);
  });
}

// ---------------------------------------------------------------------------
// Sync-Profile (Inhalt: store/sync.ts)
// ---------------------------------------------------------------------------

const syncTabelle = () => db.table('sync') as Table<SyncZeile, string>;

/**
 * Sync-Profil speichern: das Profil ohne `remote`, von den Server-Ereignissen
 * nur die neuen — in einer Transaktion. Nach einem Neuaufbau (Server
 * zurückgesetzt) wird die Liste ab der ersten Abweichung neu geschrieben.
 */
export function saveSyncProfile<T extends SyncProfilBasis>(cfg: T): Promise<void> {
  const id = cfg.tournamentId;
  return nacheinander(`s:${id}`, async () => {
    const events = cfg.remote;
    const rev = newId();
    const { remote: _r, ...rest } = cfg;
    const zeile: SyncZeile = { ...rest, remoteLength: events.length, remoteRev: rev };
    await db.transaction('rw', syncTabelle(), db.remote, async () => {
      const vorher = await syncTabelle().get(id);
      const { ab, bisher } = abWo(`s:${id}`, vorher?.remoteRev, vorher?.remoteLength, events);
      await listeSchreiben(db.remote, id, events, ab, bisher, (event, seq) => ({ tournamentId: id, seq, event }));
      await syncTabelle().put(zeile);
    });
    bekannt.set(`s:${id}`, { rev, liste: events });
  });
}

/**
 * Sync-Profil laden, `remote` wieder eingesetzt. Fehlen Server-Ereignisse,
 * wird das Profil auf „nichts bekannt" gesetzt (`seq: 0`) — der nächste
 * Abgleich holt alles neu vom Server, statt mit einer Lücke weiterzumachen.
 */
export async function loadSyncProfile<T extends SyncProfilBasis>(id: string): Promise<T | undefined> {
  const geladen = await db.transaction('r', syncTabelle(), db.remote, async () => {
    const row = await syncTabelle().get(id);
    if (!row) return undefined;
    if (typeof row.remoteLength !== 'number') return { cfg: row as unknown as T };
    const { remoteLength, remoteRev, remote: _r, ...rest } = row;
    const zeilen = await db.remote.where('[tournamentId+seq]').between([id, 0], [id, remoteLength], true, false).toArray();
    const cfg = rest as unknown as T;
    if (zeilen.length !== remoteLength || zeilen.some((z, i) => z.seq !== i)) {
      console.warn('Sync-Profil: Server-Ereignisse unvollständig — werden neu geholt', id);
      cfg.remote = [];
      (cfg as unknown as { seq: number }).seq = 0;
      return { cfg };
    }
    cfg.remote = zeilen.map((z) => z.event);
    return { cfg, rev: remoteRev };
  });
  if (!geladen) return undefined;
  if (geladen.rev) bekannt.set(`s:${id}`, { rev: geladen.rev, liste: geladen.cfg.remote });
  else bekannt.delete(`s:${id}`);
  return geladen.cfg;
}

/** Sync-Profile samt ihrer Server-Ereignisse löschen */
export async function deleteSyncProfiles(ids: readonly string[]): Promise<void> {
  for (const id of ids)
    await nacheinander(`s:${id}`, async () => {
      await db.transaction('rw', syncTabelle(), db.remote, async () => {
        await syncTabelle().delete(id);
        await alleVon(db.remote, id).delete();
      });
      bekannt.delete(`s:${id}`);
    });
}
